// =============================================================================
// Origin UI — src/pages/compliance/CompliancePage.tsx
// Compliance landing — feed-style layout with team sidebar.
// Banner → alerts → declarations/trainings (clickable cards) → policies → team.
// Training video re-watchable via modal for completed attestations.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceItemListItem,
  ComplianceOutstandingItem,
  ComplianceTeamMember,
  ComplianceReviewDueItem,
} from '@/services/complianceApi';
import styles from './compliance.module.css';

function initialsOf(name: string): string {
  return name.split(' ').filter(Boolean).map((p) => p[0]).join('').toUpperCase().slice(0, 2);
}

const ITEM_CONTEXT: Record<string, { desc: string; icon: string }> = {
  COI: {
    desc: 'Declare any directorships, shareholdings, or other business interests that may present a conflict. All employees must review their declaration annually.',
    icon: '⚖️',
  },
  GIFT: {
    desc: 'Report any gifts received or offered in connection with your work at OQT. Refer to the Gifts & Entertainment Guidance for value thresholds and rules.',
    icon: '🎁',
  },
  COC: {
    desc: 'Complete the mandatory Code of Conduct training — watch the video, read the policy, and submit your acknowledgement.',
    icon: '📋',
  },
};

export function CompliancePage() {
  const navigate = useNavigate();

  const [items, setItems] = useState<ComplianceItemListItem[]>([]);
  const [outstanding, setOutstanding] = useState<ComplianceOutstandingItem[]>([]);
  const [reviewsDue, setReviewsDue] = useState<ComplianceReviewDueItem[]>([]);
  const [team, setTeam] = useState<ComplianceTeamMember[]>([]);
  const [openError, setOpenError] = useState<string | null>(null);
  const [confirmingReview, setConfirmingReview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamLoading, setTeamLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Training video modal
  const [videoModalId, setVideoModalId] = useState<string | null>(null);
  const [videoModalTitle, setVideoModalTitle] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [itemsData, outstandingData, reviewsData] = await Promise.all([
          complianceApi.getItems(),
          complianceApi.getMyOutstanding(),
          complianceApi.getMyReviewsDue(),
        ]);
        if (!cancelled) { setItems(itemsData); setOutstanding(outstandingData); setReviewsDue(reviewsData); }
      } catch { if (!cancelled) setError('Could not load compliance data.'); }
      finally { if (!cancelled) setLoading(false); }
    }
    async function loadTeam() {
      try { const data = await complianceApi.getTeam(); if (!cancelled) setTeam(data); }
      catch { /* non-critical */ }
      finally { if (!cancelled) setTeamLoading(false); }
    }
    load();
    loadTeam();
    return () => { cancelled = true; };
  }, []);

  const trainings = items.filter((i) => i.itemType === 'Attestation');
  const declarations = items.filter((i) => i.itemType === 'Declaration');
  const policies = items.filter((i) => i.itemType === 'Policy');
  const totalOutstanding = outstanding.length + reviewsDue.length;

  async function handleViewDocument(itemId: string) {
    setOpenError(null);
    try { await complianceApi.openDocument(itemId); }
    catch { setOpenError('Could not open that document. Please try again.'); }
  }

  async function handleConfirmReview(itemId: string) {
    setConfirmingReview(itemId);
    try {
      await complianceApi.confirmReview(itemId);
      setReviewsDue((prev) => prev.filter((r) => r.complianceItemId !== itemId));
    } catch { setError('Could not confirm review. Please try again.'); }
    finally { setConfirmingReview(null); }
  }

  function openVideoModal(synthesiaVideoId: string, title: string) {
    setVideoModalId(synthesiaVideoId);
    setVideoModalTitle(title);
  }

  if (loading) {
    return <div className={styles.landing}><div className={styles.loadingFull}>Loading compliance…</div></div>;
  }

  return (
    <div className={styles.landing}>

      {/* ═══ Banner ═══ */}
      <div className={styles.banner}>
        <div className={styles.bannerInner}>
          <div className={styles.bannerText}>
            <h1 className={styles.bannerTitle}>
              <span className={styles.bannerShield}>🛡️</span>
              Compliance
            </h1>
            <p className={styles.bannerSub}>
              Policies, trainings, and declarations
            </p>
          </div>
          {totalOutstanding > 0 && (
            <div className={styles.bannerBadge}>
              <span className={styles.bannerBadgeNum}>{totalOutstanding}</span>
              <span className={styles.bannerBadgeLabel}>needs your attention</span>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Outstanding alerts ═══ */}
      {totalOutstanding > 0 && (
        <div className={styles.alertSection}>
          {outstanding.map((o) => (
            <div key={o.complianceAssignmentId} className={styles.alertCard} onClick={() => navigate(`/compliance/attest/${o.complianceItemId}`)}>
              <div className={styles.alertAccent} />
              <div className={styles.alertBody}>
                <span className={styles.alertFlag}>Action required</span>
                <span className={styles.alertTitle}>{o.title}</span>
              </div>
              <span className={styles.alertArrow}>Complete now →</span>
            </div>
          ))}
          {reviewsDue.map((r) => (
            <div key={`rev-${r.complianceItemId}`} className={styles.alertCardReview}>
              <div className={`${styles.alertAccent} ${styles.alertAccentAmber}`} />
              <div className={styles.alertBody}>
                <span className={styles.alertFlagAmber}>Review due</span>
                <span className={styles.alertTitle}>Review your {r.title}</span>
                <span className={styles.alertMeta}>
                  Last submitted {new Date(r.lastSubmissionDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div className={styles.alertActions}>
                <button
                  className={styles.alertBtnSecondary}
                  onClick={() => handleConfirmReview(r.complianceItemId)}
                  disabled={confirmingReview === r.complianceItemId}
                >
                  {confirmingReview === r.complianceItemId ? 'Confirming…' : 'No changes'}
                </button>
                <button className={styles.alertBtn} onClick={() => navigate(`/compliance/declaration/${r.complianceItemId}?prefill=true`)}>
                  Update →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div className={styles.feedError}>{error}</div>}

      {/* ═══ Two-column: content + team ═══ */}
      <div className={styles.feedLayout}>
        <div className={styles.feedMain}>

          {/* ─── Declarations ─── */}
          {declarations.length > 0 && (
            <div className={styles.storySection}>
              <h2 className={styles.sectionLabel}>Declarations</h2>
              <div className={styles.storyRow}>
                {declarations.map((d) => {
                  const ctx = ITEM_CONTEXT[d.code];
                  return (
                    <div
                      key={d.complianceItemId}
                      className={styles.storyCard}
                      onClick={() => navigate(`/compliance/declaration/${d.complianceItemId}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && navigate(`/compliance/declaration/${d.complianceItemId}`)}
                    >
                      <div className={styles.storyIconArea}>
                        <span className={styles.storyEmoji}>{ctx?.icon || '📝'}</span>
                      </div>
                      <div className={styles.storyContent}>
                        <h3 className={styles.storyTitle}>{d.title}</h3>
                        <p className={styles.storyDesc}>{ctx?.desc || 'Submit this declaration to the Compliance team.'}</p>
                        <span className={styles.storyLink}>Submit declaration →</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Trainings ─── */}
          {trainings.length > 0 && (
            <div className={styles.storySection}>
              <h2 className={styles.sectionLabel}>Compliance trainings</h2>
              <div className={styles.storyRow}>
                {trainings.map((t) => {
                  const ctx = ITEM_CONTEXT[t.code];
                  const isOutstandingItem = outstanding.some((o) => o.complianceItemId === t.complianceItemId);
                  const dest = isOutstandingItem
                    ? `/compliance/attest/${t.complianceItemId}`
                    : null;
                  return (
                    <div
                      key={t.complianceItemId}
                      className={`${styles.storyCard} ${styles.storyCardTraining}`}
                      onClick={() => dest ? navigate(dest) : handleViewDocument(t.complianceItemId)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && (dest ? navigate(dest) : handleViewDocument(t.complianceItemId))}
                    >
                      <div className={`${styles.storyIconArea} ${styles.storyIconTraining}`}>
                        <span className={styles.storyEmoji}>{ctx?.icon || '📋'}</span>
                      </div>
                      <div className={styles.storyContent}>
                        <h3 className={styles.storyTitle}>{t.title}</h3>
                        <p className={styles.storyDesc}>{ctx?.desc || 'Complete this compliance training.'}</p>
                        <span className={`${styles.storyLink} ${isOutstandingItem ? styles.storyLinkPrimary : ''}`}>
                          {isOutstandingItem ? 'Complete training →' : 'View document'}
                        </span>
                        {/* Re-watch training video — visible even after completion */}
                        {!isOutstandingItem && t.synthesiaVideoId && (
                          <span
                            className={styles.storyLink}
                            style={{ display: 'block', marginTop: '4px' }}
                            onClick={(e) => { e.stopPropagation(); openVideoModal(t.synthesiaVideoId!, t.title); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openVideoModal(t.synthesiaVideoId!, t.title); } }}
                            role="button"
                            tabIndex={0}
                          >
                            🎬 Watch training video
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Policies ─── */}
          {policies.length > 0 && (
            <div className={styles.storySection}>
              <h2 className={styles.sectionLabel}>Company policies</h2>
              {openError && <div className={styles.feedError}>{openError}</div>}
              <div className={styles.policyGrid}>
                {policies.map((p) => (
                  <div
                    key={p.complianceItemId}
                    className={styles.policyCard}
                    onClick={() => handleViewDocument(p.complianceItemId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleViewDocument(p.complianceItemId)}
                  >
                    <div className={styles.policyCardIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <span className={styles.policyCardTitle}>{p.title}</span>
                    <span className={styles.policyCardArrow}>→</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ─── Team sidebar ─── */}
        <aside className={styles.feedAside}>
          {!teamLoading && team.length > 0 && (
            <div className={styles.teamPanel}>
              <div className={styles.teamPanelHead}>
                <h2 className={styles.teamPanelTitle}>Compliance team</h2>
                <a href="mailto:trdcompliance@oq.com" className={styles.teamPanelEmail}>trdcompliance@oq.com</a>
              </div>
              {team.map((m) => (
                <div key={m.employeeId} className={styles.teamMember}>
                  <div className={styles.teamAvatar}>{initialsOf(m.name)}</div>
                  <div className={styles.teamInfo}>
                    <span className={styles.teamName}>{m.name}</span>
                    <span className={styles.teamRole}>{m.jobTitle}</span>
                    <span className={styles.teamLoc}>{m.office}</span>
                    {m.phone && <span className={styles.teamPhone}>{m.phone}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>

      {/* ═══ Training Video Modal ═══ */}
      {videoModalId && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setVideoModalId(null)}
        >
          <div
            style={{
              background: '#fff', borderRadius: '12px', width: '90vw', maxWidth: '900px',
              overflow: 'hidden', position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#0A2240' }}>{videoModalTitle}</h3>
              <button
                onClick={() => setVideoModalId(null)}
                style={{
                  background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer',
                  color: '#666', lineHeight: 1, padding: '0 4px',
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={`https://share.synthesia.io/embeds/videos/${videoModalId}`}
                title="Compliance Training Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
